/**
 * TEMPORARY in-site rates for Local (station / airport transfers) and Rental
 * (hourly packages).
 *
 * WHY THIS EXISTS: the fleets booking backend currently has only a partial rate
 * config for these two products (one cab type each, and no Gujarat rental at
 * all), so the results page showed a single option or none. Until the backend
 * has full rate coverage, we generate all three cab types here so customers can
 * see and book every option. These are PLACEHOLDER fares, approved as temporary
 * by the business ("enter a rate for now, we'll change it later").
 *
 * TO REMOVE LATER: once fleets returns all cab types for trip_type 1 and 2,
 * delete the injection block in CabAction.getCabs and this file. Outstation
 * (trip_type 3) never uses this.
 *
 * vehicle_id values are the real fleets DB ids (Hatch Back 49, Sedan 50,
 * SUV 51) so bookings map to the correct vehicle on the backend.
 */

const CAB_TYPES = [
  { vehicle_id: 49, name: "Hatch Back", seat_capacity: "4", info: "Swift, WagonR or similar" },
  { vehicle_id: 50, name: "Sedan", seat_capacity: "4", info: "Dzire, Etios or similar" },
  { vehicle_id: 51, name: "SUV", seat_capacity: "6", info: "Innova, Ertiga or similar" },
]

// Rental: per-hour package rate + per-km beyond the included km (hours x 10).
const RENTAL_RATE = {
  49: { perHour: 210, farePerKm: 11 },
  50: { perHour: 290, farePerKm: 13 },
  51: { perHour: 380, farePerKm: 17 },
}

// Local = airport / railway-station transfer. Distance-based fare per cab:
//   fare = max(minFare, baseFare + perKm * distanceKm)   [+ GST]
// TEMP placeholder rates — swap for the real per-airport / per-km numbers.
const TRANSFER_RATE = {
  49: { baseFare: 150, perKm: 13, minFare: 399 }, // Hatch Back
  50: { baseFare: 200, perKm: 16, minFare: 499 }, // Sedan
  51: { baseFare: 300, perKm: 20, minFare: 699 }, // SUV
}
const ROAD_FACTOR = 1.25 // straight-line distance -> approximate road distance
const DEFAULT_TRANSFER_KM = 20 // fallback when pickup/drop coordinates are missing

// Great-circle distance in km between two lat/lng points.
const haversineKm = (lat1, lng1, lat2, lng2) => {
  const R = 6371
  const toRad = (d) => (d * Math.PI) / 180
  const dLat = toRad(lat2 - lat1)
  const dLng = toRad(lng2 - lng1)
  const a = Math.sin(dLat / 2) ** 2 + Math.cos(toRad(lat1)) * Math.cos(toRad(lat2)) * Math.sin(dLng / 2) ** 2
  return 2 * R * Math.asin(Math.sqrt(a))
}

// Approx road distance (km) between the customer location and the hub, from the
// trip payload's pickup/drop coordinates. null when coordinates are missing.
const transferDistanceKm = (params = {}) => {
  const nums = [params.pickup_lat, params.pickup_long, params.drop_lat, params.drop_long].map(Number)
  if (nums.some((n) => !Number.isFinite(n) || n === 0)) return null
  return Math.ceil(haversineKm(nums[0], nums[1], nums[2], nums[3]) * ROAD_FACTOR)
}

const GST_PCT = 5
const round2 = (n) => Math.round(n * 100) / 100

const makeCab = (type, subtotal, farePerKm, extraFields) => {
  const gst = round2((subtotal * GST_PCT) / 100)
  const total = round2(subtotal + gst)
  return {
    vehicle_id: type.vehicle_id,
    name: type.name,
    seat_capacity: type.seat_capacity,
    info: type.info,
    fare_per_km: farePerKm.toFixed(2),
    gst_percentage: GST_PCT,
    advance_booking: 20,
    subtotal: subtotal.toFixed(2),
    gst: gst.toFixed(2),
    total: total.toFixed(2),
    displayPrice: total.toFixed(2),
    image: null,
    __temp_rate: true,
    ...extraFields,
  }
}

export const buildTempRentalCabs = (hours) => {
  const h = Number(hours) || 8
  const kmIncluded = h * 10
  return CAB_TYPES.map((type) => {
    const r = RENTAL_RATE[type.vehicle_id]
    const subtotal = r.perHour * h
    return makeCab(type, subtotal, r.farePerKm, {
      package_hours: h,
      km_included: kmIncluded,
      flat_price: subtotal,
    })
  })
}

export const buildTempLocalCabs = (params = {}) => {
  const km = transferDistanceKm(params) ?? DEFAULT_TRANSFER_KM
  return CAB_TYPES.map((type) => {
    const r = TRANSFER_RATE[type.vehicle_id]
    const subtotal = Math.max(r.minFare, Math.round(r.baseFare + r.perKm * km))
    return makeCab(type, subtotal, r.perKm, { local_base_fare: r.baseFare, transfer_km: km })
  })
}

/**
 * Returns a full 3-cab list for local (1) or rental (2), or null for anything
 * else (outstation keeps its real backend cabs).
 */
export const buildTempCabs = (tripType, params = {}) => {
  const t = Number(tripType)
  if (t === 2) return buildTempRentalCabs(params.package_type_in_hours)
  if (t === 1) return buildTempLocalCabs(params)
  return null
}
